import { Chip } from '@mui/material';
import Link from 'components/Link';
import { IColumn } from 'kosmos/components/ResourceTable';
import { ISchema } from 'kosmos/dialogs/CreateResourceModal';
import { get } from 'lodash';
import {
  AuditLogDto,
  AuditLogSummaryDto,
  CompanyListShortListDto,
  CompanyUpdateSubscriptionDto,
  OrganisationDto,
  OrganisationScoredCompaniesDto,
  UserDto,
} from 'models/dtoTypes';
import { date2str } from 'utils/date';
import { getKosmosReportLink } from 'utils/report';

// User Role Name Mapping
const userRoleNameMapper = {
  tfp: 'TFP User',
  servicetfp: 'TFP Service API',
  orgadmin: 'Organisation Admin',
  admin: 'Global Admin',
  sme: 'Business',
};

// User Schema
export const userCreateSchema = (t: (key: string) => string): ISchema => ({
  fields: [
    { label: t('first-name'), name: 'firstName', widget: 'text', required: true },
    { label: t('last-name'), name: 'lastName', widget: 'text', required: true },
    { label: t('email'), name: 'email', widget: 'text', required: true },
    {
      label: t('role'),
      name: 'userRole',
      widget: 'select',
      required: true,
      placeholder: t('select-the-user-role'),
      description: t(
        'user-role-under-this-organisation-tfp-service-api-will-require-extra-setup-and-its-role-cannot-be-updated-later',
      ),
      options: [
        { alias: 'sme', label: t('business') },
        { alias: 'tfp', label: t('tfp-user') },
        { alias: 'serviceTfp', label: t('tfp-service-api') },
        { alias: 'orgAdmin', label: t('organisation-admin') },
      ],
    },
  ],
});

// User Columns
export const generateUserColumns = (
  t: (key: string) => string,
  organisation: OrganisationDto,
  options: { isAdmin: boolean },
  actions: {
    handleEnableUser: (user: UserDto) => Promise<UserDto | undefined>;
    handleDisableUser: (user: UserDto) => Promise<UserDto | undefined>;
  },
): IColumn<UserDto>[] => {
  const isActive = organisation.status === 'Active';
  const { isAdmin } = options;
  const { handleEnableUser, handleDisableUser } = actions;

  return [
    { label: t('email'), dataIndex: 'email', sortIndex: 'email' },
    { label: t('first-name'), dataIndex: 'firstName' },
    { label: t('last-name'), dataIndex: 'lastName' },
    {
      label: t('role'),
      dataIndex: 'userRole',
      render: (user) => (
        <span>{get(userRoleNameMapper, user.userRole.toLowerCase()) || user.userRole}</span>
      ),
    },
    { label: t('active-directory-id'), dataIndex: 'aadUserId' },
    {
      label: t('account-enabled'),
      render: (user) => (
        <Chip
          label={user.accountEnabled ? t('yes') : t('no')}
          color={user.accountEnabled ? 'primary' : 'default'}
        />
      ),
    },
    ...(isAdmin
      ? [
          {
            label: t('action'),
            actions: [
              {
                label: t('enable'),
                hide: (user: UserDto) => !isActive || !user.aadUserId || user.accountEnabled,
                onClick: handleEnableUser,
              },
              {
                label: t('disable'),
                hide: (user: UserDto) => !isActive || !user.aadUserId || !user.accountEnabled,
                onClick: handleDisableUser,
              },
            ],
          },
        ]
      : []),
  ];
};

// Company List Schema
export const companyListCreateSchema = (t: (key: string) => string): ISchema => ({
  fields: [
    { label: t('list-name'), name: 'name', required: true, widget: 'text' },
    { label: t('description'), name: 'description', required: true, widget: 'text' },
  ],
});

// Callback Schema
export const callbackCreateSchema = (t: (key: string) => string): ISchema => ({
  fields: [
    { label: t('Callback'), name: 'callbackUrl', required: true, widget: 'text' },
    { label: t('Client Certification Base64'), name: 'clientCertificationBase64', required: false, widget: 'textArea' },
    { label: t('Passphrase'), name: 'passphrase', required: false, widget: 'text' },
  ],
});

// Company List Columns
export const generateCompanyListColumns = (
  t: (key: string) => string,
): IColumn<CompanyListShortListDto>[] => [
  {
    label: t('name'),
    sortIndex: 'name',
    render: (model) => <Link to={`/companyLists/${model.id}`} text={model.name || ''} />,
  },
  { label: t('description'), dataIndex: 'description' },
  { label: t('company-count'), dataIndex: 'companyCount' },
];

// Callback Columns
export const generateCallbackColumns = (
  t: (key: string) => string,
  handleDeleteCallback: (
    user: CompanyUpdateSubscriptionDto,
  ) => Promise<CompanyUpdateSubscriptionDto | undefined>,
): IColumn<CompanyUpdateSubscriptionDto>[] => [
  { label: t('Callback Url'), sortIndex: 'callbackUrl', dataIndex: 'callbackUrl' },
  { label: t('Client Certificate Enabled'), render: (callback) => callback.clientCertificationBase64 ? t('yes') : t('no') },
  { label: t('action'), actions: [{ label: t('Delete'), onClick: handleDeleteCallback }] },
];

// Activity Columns
export const generateActivityColumns = (t: (key: string) => string): IColumn<AuditLogDto>[] => [
  { label: t('user'), render: (auditLog) => <>{auditLog.user?.email || auditLog.aadUserId} </> },
  { label: t('action-name'), dataIndex: 'actionName' },
  { label: t('controller'), dataIndex: 'controllerName' },
  {
    label: t('created'),
    dataIndex: 'created',
    sortIndex: 'created',
    render: (auditLog) => date2str(auditLog.created, 'DD MMM YYYY, HH:mm:ss'),
  },
  { label: t('meta'), dataIndex: 'metadata' },
];

// Audit Log Filter Schema
export const auditLogFilterSchema = (t: (key: string) => string) => [
  { label: t('action-name'), name: 'actionName', widget: 'text' },
  { label: t('user'), name: 'aadUserId', widget: 'userSelect' },
  { label: t('period'), name: 'createdMonth', widget: 'monthPicker' },
];

// Audit Log Summary Columns
export const generateAuditLogSummaryColumns = (
  t: (key: string) => string,
): IColumn<AuditLogSummaryDto>[] => [
  { label: t('action-name'), dataIndex: 'actionName' },
  { label: t('event-name'), dataIndex: 'eventName' },
  { label: t('count'), dataIndex: 'count' },
];

// Scored Companies Filter Schema
export const scoredCompaniesFilterSchema = (t: (key: string) => string) => [
  { label: t('name'), name: 'name', widget: 'text' },
  { label: t('scored-date-from'), name: 'lastScoredAtStart', widget: 'datePicker' },
  { label: t('scored-date-to'), name: 'lastScoredAtEnd', widget: 'datePicker' },
];

// Scored Companies Columns
export const generateScoredCompaniesColumns = (
  t: (key: string) => string,
  organisationId?: string,
): IColumn<OrganisationScoredCompaniesDto>[] => [
  {
    label: t('name'),
    dataIndex: 'name',
    render: (company) => (
      <Link
        to={getKosmosReportLink(company.companyId || '', organisationId)}
        text={company.companyName || 'Unknown'}
      />
    ),
    sortIndex: 'name',
  },
  { label: t('country'), dataIndex: 'companyCountry' },
  { label: t('score'), dataIndex: 'score' },
  { label: t('scoring-state'), dataIndex: 'scoringState' },
  {
    label: t('scored-at'),
    render: (company) => date2str(company.scoredAt || ''),
    sortIndex: 'scoredAt',
  },
];
